import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
    Paper,
    TextField,
    Typography,
} from '@material-ui/core';
import { alpha, makeStyles } from '@material-ui/core/styles';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useAuthProvider, useTranslate } from 'react-admin';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { tokenFound } from '../redux/actions/users';
import { CustomReducerState } from '../redux/reducers';
import { AddToHomeScreenInfoCardButton } from '../components/pwa/AddToHomeScreenInfo';
import PwaQrInstallPrompt from '../components/pwa/PwaQrInstallPrompt';
import { CRM_COLORS, CRM_RADIUS } from '../theme/crmColors';
import { BREAKPOINTS } from '../theme/breakpoints';
import { getToken, setTokens } from '../utils/token';
import { REDIRECT_TO_QUERY_PARAMS, redirect_to_ancs_v2 } from './paths';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: '560px minmax(0, 1fr)',
        backgroundColor: CRM_COLORS.surface,
        '@media (max-width:1000px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
            gridTemplateRows: 'auto minmax(0, 1fr)',
        },
    },
    aside: {
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: CRM_COLORS.secondary,
        color: CRM_COLORS.surface,
        padding: '56px 56px 48px',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
        '@media (max-width:1000px)': {
            padding: '24px 24px',
        },
    },
    asideRings: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
    },
    asideBrand: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
    },
    asideApp: {
        fontSize: '0.9375rem',
        fontWeight: 500,
        color: '#a9c7e2',
    },
    asideText: {
        position: 'relative',
        marginTop: 'auto',
        maxWidth: 420,
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        '@media (max-width:1000px)': {
            display: 'none',
        },
    },
    asideAccent: {
        width: 48,
        height: 4,
        borderRadius: 2,
        backgroundColor: CRM_COLORS.success,
    },
    asideTitle: {
        fontSize: '2.5rem',
        fontWeight: 600,
        lineHeight: 1.1,
        letterSpacing: '.01em',
        color: CRM_COLORS.surface,
    },
    asideSubtitle: {
        fontSize: '1rem',
        lineHeight: 1.5,
        color: '#c4d8ea',
    },
    main: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: CRM_COLORS.background,
        padding: theme.spacing(3),
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: theme.spacing(2),
            alignItems: 'flex-start',
        },
    },
    card: {
        width: '100%',
        maxWidth: 400,
        padding: 40,
        boxSizing: 'border-box',
        borderRadius: CRM_RADIUS.dialog,
        border: `1px solid ${CRM_COLORS.border}`,
        boxShadow: '0 8px 28px rgba(9, 43, 75, 0.08)',
        backgroundColor: CRM_COLORS.surface,
        position: 'relative',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: theme.spacing(3.5, 2.5, 3),
        },
    },
    logo: {
        display: 'block',
        width: 128,
        height: 128,
        margin: '0 auto 12px',
    },
    header: {
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        marginBottom: 22,
    },
    title: {
        fontSize: '1.875rem',
        fontWeight: 700,
        color: CRM_COLORS.secondary,
        lineHeight: 1.2,
    },
    subtitle: {
        fontSize: '0.9375rem',
        color: CRM_COLORS.text.secondary,
        lineHeight: 1.4,
    },
    field: {
        marginBottom: 22,
        '& .MuiInput-root': {
            minHeight: 46,
        },
    },
    error: {
        marginBottom: theme.spacing(2),
        textAlign: 'center',
        fontSize: '0.8125rem',
        ...CRM_COLORS.status.bad,
        borderRadius: CRM_RADIUS.control,
        padding: theme.spacing(1, 1.5),
    },
    submitButton: {
        width: '100%',
        height: 48,
        fontSize: '1rem',
        '&:disabled': {
            backgroundColor: alpha(CRM_COLORS.primary, 0.4),
            color: CRM_COLORS.surface,
        },
    },
    visibilityButton: {
        color: CRM_COLORS.text.secondary,
        padding: 8,
    },
}));

const LoginPage = () => {
    const classes = useStyles();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();
    const authProvider = useAuthProvider();
    const translate = useTranslate();
    const userConnected = useSelector((state: CustomReducerState) => state.rikutec.users.userConnected);
    const existingToken = getToken();

    const redirectPath = useMemo(() => {
        const searchParams = new URLSearchParams(location.search ? location.search.substring(1) : '');
        const redirectTo = searchParams.get(REDIRECT_TO_QUERY_PARAMS);
        const page = redirectTo || redirect_to_ancs_v2;

        return `${process.env.REACT_APP_PREFIX}p/${page}`;
    }, [location.search]);

    useEffect(() => {
        if (existingToken && !userConnected) {
            // Ensure both main and SS tokens are present (older sessions may
            // only have rikutec_token without rikutec_token_ss).
            setTokens(existingToken);
            dispatch(tokenFound.fn(existingToken));
        }
    }, [dispatch, existingToken, userConnected]);

    useEffect(() => {
        if (userConnected) {
            history.push(redirectPath);
        }
    }, [history, redirectPath, userConnected]);

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const trimmedUsername = username.trim();
        if (!trimmedUsername || !password) {
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const result = await authProvider.login({
                username: trimmedUsername,
                password,
            });

            if (result?.token) {
                dispatch(tokenFound.fn(result.token));
            }
        } catch {
            setError(translate('app.login.error'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box className={classes.root}>
            <PwaQrInstallPrompt />

            <Box component='aside' className={classes.aside}>
                <svg aria-hidden='true' viewBox='0 0 560 900' className={classes.asideRings}>
                    <g fill='none' stroke='#ffffff' strokeOpacity='0.06' strokeWidth='1.5'>
                        {[120, 200, 280, 360, 440].map((radius) => (
                            <circle key={radius} cx='520' cy='760' r={radius} />
                        ))}
                    </g>
                </svg>
                <Box className={classes.asideBrand}>
                    <span className={classes.asideApp}>{translate('app.sidebar.appName')}</span>
                </Box>
                <Box className={classes.asideText}>
                    <span className={classes.asideAccent} aria-hidden />
                    <Typography className={classes.asideTitle} component='h2'>
                        {translate('app.login.heroTitle')}
                    </Typography>
                    <Typography className={classes.asideSubtitle}>{translate('app.login.heroText')}</Typography>
                </Box>
            </Box>

            <Box component='main' className={classes.main}>
                <Paper className={classes.card} elevation={0}>
                    <AddToHomeScreenInfoCardButton />

                    <img src='/logo512.png' alt='MyANC' className={classes.logo} />

                    <Box className={classes.header}>
                        <Typography className={classes.title} component='h1'>
                            {translate('app.login.title')}
                        </Typography>
                        <Typography className={classes.subtitle}>{translate('app.login.subtitle')}</Typography>
                    </Box>

                    <form onSubmit={submit} noValidate>
                        <TextField
                            fullWidth
                            required
                            className={classes.field}
                            label={translate('app.login.username')}
                            value={username}
                            onChange={(event) => setUsername(event.target.value)}
                            autoFocus
                            autoComplete='username'
                            disabled={loading}
                        />

                        <TextField
                            fullWidth
                            required
                            className={classes.field}
                            label={translate('app.login.password')}
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(event) => setPassword(event.target.value)}
                            autoComplete='current-password'
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position='end'>
                                        <IconButton
                                            className={classes.visibilityButton}
                                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                                            onClick={() => setShowPassword((value) => !value)}
                                            onMouseDown={(event) => event.preventDefault()}
                                            disabled={loading}
                                        >
                                            {showPassword ? (
                                                <VisibilityOff fontSize='small' />
                                            ) : (
                                                <Visibility fontSize='small' />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {error && (
                            <Typography className={classes.error} role='alert'>
                                {error}
                            </Typography>
                        )}

                        <Button
                            type='submit'
                            variant='contained'
                            color='primary'
                            className={classes.submitButton}
                            disabled={!username.trim() || !password || loading}
                        >
                            {loading ? <CircularProgress size={22} color='inherit' /> : translate('app.login.submit')}
                        </Button>
                    </form>
                </Paper>
            </Box>
        </Box>
    );
};

export default LoginPage;
